import { GAME_WIDTH, GAME_HEIGHT } from '../config.js';
import { lockInput, unlockInput } from './inputLock.js';
import { audioManager } from '../audio/AudioManager.js';
import { iconText } from './iconText.js';
import { enlargeForTouch } from './touchEnlarge.js';
import { isTouchDevice } from './isTouchDevice.js';

// "How do you drive?" reference card — four tiles explaining the practical-lesson
// controls. Shown once automatically before lesson 1 (see DrivingSchoolMenu) and any
// time after that from the instructor's menu ("Jak się jeździ? 🎮" row).

const CONTROLS_SEEN_KEY = 'sd-controls-seen';

// localStorage can throw (private browsing, storage disabled) — never let a reference
// card crash the flow over it.
export function hasSeenControls() {
  try { return localStorage.getItem(CONTROLS_SEEN_KEY) === '1'; } catch { return false; }
}

function markControlsSeen() {
  try { localStorage.setItem(CONTROLS_SEEN_KEY, '1'); } catch { /* ignore — not essential */ }
}

function viewCentre(scene) {
  const cam = scene.cameras.main;
  return { ox: cam.scrollX, oy: cam.scrollY, x: cam.scrollX + GAME_WIDTH / 2, y: cam.scrollY + GAME_HEIGHT / 2 };
}

const TILES = [
  { arrows: '{arrow_left} {arrow_right}', caption: 'gaz i hamulec' },
  // Direction-independent on purpose: on the street the Yaris faces right (back is ←), on a
  // training route it faces left (back is →) — the tile has to be true on both.
  { arrows: '{arrow_left} lub {arrow_right} w tył\n(przytrzymaj)', caption: 'przytrzymaj strzałkę w TYŁ (przeciwną do jazdy) przy 0 = cofanie (R)' },
  { arrows: '{arrow_up} {arrow_down}', caption: 'zmiana pasa' },
  { arrows: 'Zawróć {back}︎', caption: 'Zawróć {back}︎ tylko na postoju' },
];
// Phones steer with the joystick (CarController reads its x for gas / brake / reverse and a
// firm push up / down for a lane) — the arrow-key tiles taught a child keys they don't have.
const TOUCH_TILES = [
  { arrows: 'joystick {arrow_left} {arrow_right}', caption: 'w bok: gaz i hamulec' },
  { arrows: 'joystick w tył\n(przytrzymaj)', caption: 'przytrzymaj joystick w TYŁ (przeciwnie do jazdy) przy 0 = cofanie (R)' },
  { arrows: 'joystick {arrow_up} {arrow_down}', caption: 'pchnij w górę lub w dół: zmiana pasa' },
  TILES[3],
];

const W = 580, H = 460;
const TILE_GAP_X = 24, TILE_GAP_Y = 20;
const GRID_TOP = 78;
const TILE_W = (W - 60 - TILE_GAP_X) / 2;
const TILE_H = 148;

/**
 * Show the controls reference card. Resolves once the child dismisses it (any of the
 * ✕, overlay, or "Zrozumiano!" button) — there is nothing to confirm/cancel, so unlike
 * showModal/showLessonBriefing this always resolves with no value.
 */
export function showControlsCard(scene) {
  return new Promise((resolve) => {
    const { ox, oy, x, y } = viewCentre(scene);
    const layer = scene.add.container(0, 0).setDepth(2000);
    lockInput(scene);

    const overlay = scene.add.rectangle(ox, oy, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.6)
      .setOrigin(0, 0).setInteractive();
    const box = scene.add.rectangle(x, y, W, H, 0xffffff, 1).setStrokeStyle(4, 0x333333);
    const title = iconText(scene, x, y - H / 2 + 26, 'Jak się jeździ? {gamepad}', {
      fontFamily: 'system-ui', fontSize: '26px', color: '#000', fontStyle: 'bold',
    }).setOrigin(0.5, 0);
    layer.add([overlay, box, title]);
    enlargeForTouch(scene, layer, { x, y, w: W, h: H }, { overlay });

    const gridLeft = x - W / 2 + 30;
    const gridTop = y - H / 2 + GRID_TOP;
    (isTouchDevice() ? TOUCH_TILES : TILES).forEach((tile, i) => {
      const col = i % 2, row = Math.floor(i / 2);
      const cx = gridLeft + TILE_W / 2 + col * (TILE_W + TILE_GAP_X);
      const cy = gridTop + TILE_H / 2 + row * (TILE_H + TILE_GAP_Y);

      const g = scene.add.graphics();
      g.fillStyle(0xfff4d6, 1).fillRoundedRect(cx - TILE_W / 2, cy - TILE_H / 2, TILE_W, TILE_H, 14);
      g.lineStyle(2, 0xd8a93a, 1).strokeRoundedRect(cx - TILE_W / 2, cy - TILE_H / 2, TILE_W, TILE_H, 14);

      const arrows = iconText(scene, cx, cy - 34, tile.arrows, {
        fontFamily: 'system-ui', fontSize: '26px', color: '#000', fontStyle: 'bold', align: 'center',
        wordWrap: { width: TILE_W - 20 },
      }).setOrigin(0.5, 0);
      // Icons are wider than the old arrow glyphs: a two-line label shrinks to the room above the caption.
      if (arrows.height > 56) arrows.setScale(56 / arrows.height);
      const caption = iconText(scene, cx, cy + 24, tile.caption, {
        fontFamily: 'system-ui', fontSize: '14px', color: '#333', align: 'center',
        wordWrap: { width: TILE_W - 20 },
      }).setOrigin(0.5, 0);
      layer.add([g, arrows, caption]);
    });

    const btnY = y + H / 2 - 40;
    const okBtn = scene.add.rectangle(x, btnY, W - 80, 50, 0xffd166)
      .setStrokeStyle(2, 0x333333).setInteractive({ useHandCursor: true });
    const okTxt = iconText(scene, x, btnY, 'Zrozumiano! {thumbs_up}', {
      fontFamily: 'system-ui', fontSize: '20px', color: '#000', fontStyle: 'bold',
    }).setOrigin(0.5);
    layer.add([okBtn, okTxt]);

    const closeBtn = scene.add.rectangle(x + W / 2 - 24, y - H / 2 + 24, 36, 36, 0xaa3333, 1)
      .setStrokeStyle(2, 0x000000).setInteractive({ useHandCursor: true });
    const closeTxt = iconText(scene, x + W / 2 - 24, y - H / 2 + 24, '×', {
      fontFamily: 'system-ui', fontSize: '20px', color: '#fff', fontStyle: 'bold',
    }).setOrigin(0.5);
    layer.add([closeBtn, closeTxt]);

    const close = () => {
      audioManager.playSfx('click');
      markControlsSeen();
      unlockInput(scene);
      layer.destroy();
      resolve();
    };
    okBtn.on('pointerup', close);
    closeBtn.on('pointerup', close);
    overlay.on('pointerup', close);
  });
}
